import ApiRequest from '../../Decorators/ApiRequest';
import { ApiResult } from '@shared/Interfaces/Api/ApiResult';
import { SortParamEnum } from '@shared/Enums/SortParamEnum';
import { ItemTypesResponse } from './Interfaces/ItemTypesResponse';
import { FetchItemTypesArgs } from './Interfaces/FetchItemTypesArgs';
import { UnitEnum } from '@shared/Enums/UnitEnum';
import { RawItemTypeWithItems } from '../../../Stores/ProjectStore';
import { API_DEFAULT_HEADERS } from '@configs/ApiDefaultHeaders';
import { ApiArguments } from '@shared/Interfaces/Api/ApiArguments';
import { RawItemType } from '@entities/ItemType/Types';
import {
    ApiPaginatedData,
    ApiPaginatedResult,
} from '@shared/Interfaces/Api/ApiPaginatedResult';
import { FetchItemTypesAllArgs } from './Interfaces/FetchItemTypesAllArgs';

const BASE_URL = import.meta.env.VITE_BASE_URL_CUSTOMER;

export class ItemTypeGateway {
    async getItemTypeItems({
        itemType,
        page = 1,
        search,
        sortOrder = SortParamEnum.ASC,
        perPage = 4,
        projectStoreId,
    }: FetchItemTypesArgs): Promise<ApiResult<ItemTypesResponse>> {
        const shouldIncludeUnit =
            itemType.unit &&
            itemType.unit !== UnitEnum.EACH &&
            itemType.unit !== UnitEnum.PSC;

        const queryParams = new URLSearchParams({
            page: page.toString(),
            perPage: perPage.toString(),
            sortField: 'price',
            sortOrder,
            ...(projectStoreId && {
                storeIdFromProjects: projectStoreId.toString(),
            }),
        });

        if (search) {
            queryParams.append('title', encodeURIComponent(search));
        }

        if (shouldIncludeUnit) {
            queryParams.append('packaging', itemType.unit);
        }

        const url = `${BASE_URL}/item_types/${itemType.id}?${queryParams.toString()}`;

        try {
            const response = await ApiRequest(url, {
                method: 'GET',
            });

            const json = await response.json();
            if (response.status !== 200 && response.status !== 201) {
                return {
                    error: true,
                    message: 'Failed to fetch item types',
                };
            }

            return {
                error: false,
                data: json as ItemTypesResponse,
            };
        } catch (error) {
            return {
                error: true,
                message: `Error: ${error}`,
            };
        }
    }

    async getItemTypesAll({
        page,
        perPage = 1,
        itemTypeIds,
        projectStoreId,
    }: FetchItemTypesAllArgs): Promise<ApiResult<RawItemTypeWithItems[]>> {
        if (itemTypeIds.length === 0) {
            return { error: true, message: 'No item types found!' };
        }

        const queryParams = new URLSearchParams({
            page: page.toString(),
            perPage: perPage.toString(),
            storeIdFromProjects: projectStoreId.toString(),
        });

        const body = { item_type_ids: itemTypeIds };

        const url = `${BASE_URL}/item_types_all?${queryParams.toString()}`;

        try {
            const response = await ApiRequest(url, {
                method: 'POST',
                body: JSON.stringify(body),
                headers: API_DEFAULT_HEADERS,
            });

            const json = await response.json();
            if (response.status !== 200 && response.status !== 201) {
                return {
                    error: true,
                    message: 'Failed to fetch all item types',
                };
            }

            return {
                error: false,
                data: json.data as RawItemTypeWithItems[],
            };
        } catch (error) {
            return {
                error: true,
                message: `Error: ${error}`,
            };
        }
    }

    async getItemTypes({
        page = 1,
        perPage = 4,
        search,
    }: ApiArguments): Promise<ApiPaginatedResult<RawItemType[]>> {
        const queryParams = new URLSearchParams({
            page: page.toString(),
            perPage: perPage.toString(),
        });

        if (search) {
            queryParams.append('title', encodeURIComponent(search));
        }

        const url = `${BASE_URL}/item_types?${queryParams.toString()}`;

        try {
            const response = await ApiRequest(url, {
                method: 'GET',
            });

            const json = await response.json();
            if (response.status !== 200 && response.status !== 201) {
                return {
                    error: true,
                    message: 'Failed to fetch item types',
                };
            }

            return {
                error: false,
                data: json as ApiPaginatedData<RawItemType[]>,
            };
        } catch (error) {
            return {
                error: true,
                message: `Error: ${error}`,
            };
        }
    }
}
